import axios from 'axios';

const base = () => process.env.REACT_APP_API_URL ?? '';

export type TeacherLetterCohort = 'all' | 'finalist_teachers' | 'laureate_teachers' | 'manual' | 'no_zip_excluded';

export interface TeacherLetterSettings {
	/** Blok nagłówka komitetu (kilka linii, wyśrodkowany — jak we wzorze listu). */
	committee_header_block: string;
	/** Logo nad nagłówkiem: brak, standardowe z zaświadczeń albo dedykowane dla listu. */
	logo_variant?: 'none' | 'standard' | 'letter';
	/** Wsteczna kompatybilność: true odpowiada logo_variant="standard". */
	show_header_logo?: boolean;
	/** Linia miejsca i daty, wyrównana do prawej (np. „Warszawa, dnia 12 czerwca 2026 r.”). */
	issue_line: string;
	doc_title: string;
	/** Akapit stały nr 2; tokeny [forma żeńska/forma męska] podmieniane wg Pani/Pan, np. „o [Pani/Pana] rozległej wiedzy”. */
	body_paragraph_2: string;
	/** Akapit stały nr 3 (gratulacje i życzenia). */
	body_paragraph_3: string;
	signatory_title: string;
	signatory_name: string;
	/** Opcjonalnie: kolumny Platnik* w szablonie importu XLS Elektronicznego Nadawcy (nadawca / płatnik). */
	en_xlsx_platnik_rodzaj?: string;
	en_xlsx_platnik_nazwa?: string;
	en_xlsx_platnik_nazwa_cd?: string;
	en_xlsx_platnik_ulica?: string;
	en_xlsx_platnik_dom?: string;
	en_xlsx_platnik_lokal?: string;
	en_xlsx_platnik_kod?: string;
	en_xlsx_platnik_miejscowosc?: string;
	en_xlsx_platnik_nip?: string;
	/** Konwersja etykiet EN → arkusz: przesunięcie siatki w dół (+) / w górę (−), mm (−20…40). */
	en_label_sheet_offset_top_mm?: number;
	/** Dodatkowy odstęp między rzędami etykiet na arkuszu, mm (0…10). */
	en_label_sheet_row_gap_extra_mm?: number;
}

export interface TeacherLetterStudentInfo {
	student_id: number;
	first_name: string;
	last_name: string;
	personal_title: string;
	final_title: string;
	class_roman: string;
	school_name: string;
}

export interface TeacherLetterRow {
	teacher_id: number;
	personal_title: string;
	academic_degree: string;
	first_name: string;
	last_name: string;
	email: string;
	phone: string;
	district_name: string;
	/** Afiliacja z rejestracji nauczyciela (źródło linii szkoły). */
	affiliation: string;
	address_city: string;
	address_zip: string;
	address_street: string;
	address_building: string;
	address_apartment: string;
	is_pan: boolean;
	/** Zatwierdzeni uczniowie z tytułem końcowym (treść akapitu). */
	students: TeacherLetterStudentInfo[];
	students_with_title: number;
	/** Liczba różnych szkół uczniów — >1 to kandydat do ręcznej korekty akapitu. */
	distinct_schools: number;
	salutation: string;
	addressee_name_line: string;
	school_line: string;
	achievement_paragraph: string;
	/** Pomiń w zbiorczym ZIP / PDF (np. list wręczony osobiście). */
	exclude_from_bulk_export: boolean;
	/** Kohorta ręczna (odpowiednik R1 w zaświadczeniach). */
	manual_cohort: boolean;
	has_letter_row: boolean;
	/** Propozycje z bazy / morfologii — tylko w odpowiedzi szczegółowej (GET /teachers/{id}). */
	salutation_suggested?: string;
	addressee_name_line_suggested?: string;
	school_line_suggested?: string;
	achievement_paragraph_suggested?: string;
}

export const getTeacherLetterSettings = async (token: string | null) => {
	const url = `${base()}/stage3-teacher-letters/settings`;
	return axios.get<TeacherLetterSettings>(url, {
		headers: { Authorization: `Bearer ${token}` },
	});
};

export const saveTeacherLetterSettings = async (token: string | null, data: Partial<TeacherLetterSettings>) => {
	const url = `${base()}/stage3-teacher-letters/settings`;
	return axios.post<TeacherLetterSettings>(url, data, {
		headers: { Authorization: `Bearer ${token}` },
	});
};

export const getTeacherLetterRows = async (token: string | null, cohort: TeacherLetterCohort) => {
	const url = `${base()}/stage3-teacher-letters/teachers`;
	return axios.get<{ teachers: TeacherLetterRow[] }>(url, {
		params: { cohort },
		headers: { Authorization: `Bearer ${token}` },
	});
};

/** Pełny wiersz z propozycjami (akapit liczony z morfologią — wolniejsze) — np. przed otwarciem edycji. */
export const getTeacherLetterDetail = async (token: string | null, teacherId: number) => {
	const url = `${base()}/stage3-teacher-letters/teachers/${teacherId}`;
	return axios.get<{ ok: boolean; error?: string; teacher: TeacherLetterRow }>(url, {
		headers: { Authorization: `Bearer ${token}` },
	});
};

/** Mniejsze partie niż w zaświadczeniach — na nauczyciela przypada kilka wywołań morfologii (imiona i nazwiska uczniów). */
export const TEACHER_LETTER_SYNC_BATCH_SIZE = 25;

export interface TeacherLetterSyncResult {
	updated: number;
	errors?: { teacher_id: number; message: string }[];
	error?: string;
	message?: string;
	total?: number;
	offset?: number;
	limit?: number;
	batch_processed?: number;
	processed_total?: number;
	done?: boolean;
}

export const syncTeacherLetterTexts = async (
	token: string | null,
	cohort: TeacherLetterCohort,
	offset = 0,
	limit = TEACHER_LETTER_SYNC_BATCH_SIZE,
	fillMissingOnly = false,
) => {
	const url = `${base()}/stage3-teacher-letters/sync-texts`;
	const body: Record<string, unknown> = { cohort, offset, limit };
	if (fillMissingOnly) {
		body.fill_missing_only = true;
	}
	return axios.post<TeacherLetterSyncResult>(url, body, {
		headers: { Authorization: `Bearer ${token}` },
	});
};

export const updateTeacherLetterRow = async (
	token: string | null,
	teacherId: number,
	fields: Partial<
		Pick<TeacherLetterRow, 'salutation' | 'addressee_name_line' | 'school_line' | 'achievement_paragraph'>
	> & {
		exclude_from_bulk_export?: boolean;
		manual_cohort?: boolean;
	},
) => {
	const url = `${base()}/stage3-teacher-letters/row/${teacherId}`;
	return axios.put<{ ok?: boolean; teacher?: TeacherLetterRow; error?: string }>(url, fields, {
		headers: { Authorization: `Bearer ${token}` },
	});
};

export interface TeacherLetterPreviewHtml {
	html: string;
	teacher_id: number;
	first_name: string;
	last_name: string;
}

export const getTeacherLetterPreviewHtml = async (token: string | null, teacherId: number) => {
	const url = `${base()}/stage3-teacher-letters/preview-html`;
	return axios.get<TeacherLetterPreviewHtml>(url, {
		params: { teacher_id: teacherId },
		headers: { Authorization: `Bearer ${token}` },
	});
};

export const exportTeacherLetterSinglePdf = async (token: string | null, teacherId: number) => {
	const url = `${base()}/stage3-teacher-letters/preview-pdf`;
	return axios.get<{ filename: string; file_base64: string }>(url, {
		params: { teacher_id: teacherId },
		headers: { Authorization: `Bearer ${token}` },
	});
};

export const exportTeacherLetterZip = async (token: string | null, cohort: TeacherLetterCohort) => {
	const url = `${base()}/stage3-teacher-letters/export-zip`;
	return axios.post<{ filename: string; file_base64: string }>(
		url,
		{ cohort },
		{
			headers: { Authorization: `Bearer ${token}` },
		},
	);
};

/** Jeden plik PDF — wszystkie listy kohorty (do zbiorczego wydruku i podpisu). */
export const exportTeacherLetterMergedPdf = async (token: string | null, cohort: TeacherLetterCohort) => {
	const url = `${base()}/stage3-teacher-letters/export-merged-pdf`;
	return axios.post<{ filename: string; file_base64: string }>(
		url,
		{ cohort },
		{
			headers: { Authorization: `Bearer ${token}` },
		},
	);
};

export const exportTeacherLetterCsv = async (token: string | null, cohort: TeacherLetterCohort) => {
	const url = `${base()}/stage3-teacher-letters/export-csv`;
	return axios.get<{ filename: string; file_base64: string }>(url, {
		params: { cohort },
		headers: { Authorization: `Bearer ${token}` },
	});
};

export type TeacherLetterShippingPostEnParams = {
	en_format: 'S' | 'M' | 'L';
	en_masa_kg: number;
	en_kategoria: string;
	en_uwagi?: string;
	en_file_format: 'xls' | 'xml';
	iwd_symbol?: string;
	iwd_karta_umowy?: string;
	iwd_umowa?: string;
};

/** Import Elektroniczny Nadawca — adresat = adres korespondencyjny nauczyciela z rejestracji. */
export const exportTeacherLetterShippingPostXlsx = async (
	token: string | null,
	cohort: TeacherLetterCohort,
	params: TeacherLetterShippingPostEnParams,
) => {
	const url = `${base()}/stage3-teacher-letters/export-shipping-post-xlsx`;
	const body: Record<string, unknown> = {
		cohort,
		en_format: params.en_format,
		en_masa_kg: params.en_masa_kg,
		en_kategoria: params.en_kategoria,
		en_uwagi: params.en_uwagi ?? '',
		en_file_format: params.en_file_format,
	};
	if (params.en_file_format === 'xml') {
		body.iwd_symbol = params.iwd_symbol ?? '';
		body.iwd_karta_umowy = params.iwd_karta_umowy ?? '';
		body.iwd_umowa = params.iwd_umowa ?? '';
	}
	return axios.post<{ filename: string; file_base64: string }>(url, body, {
		headers: { Authorization: `Bearer ${token}` },
	});
};

/** PDF z EN (jedna etykieta na stronę) → arkusz A4, siatka 105×57 mm (2×5) — ustawienia siatki z listów. */
export const convertTeacherLetterEnLabelsPdfToSheet = async (token: string | null, fileBase64: string) => {
	const url = `${base()}/stage3-teacher-letters/convert-en-labels-pdf`;
	return axios.post<{ filename: string; file_base64: string }>(
		url,
		{ file_base64: fileBase64 },
		{
			headers: { Authorization: `Bearer ${token}` },
		},
	);
};
